import {create} from 'zustand';
import {devtools} from 'zustand/middleware';
import {immer} from 'zustand/middleware/immer';
import {shallow} from 'zustand/shallow';
import {local} from 'lib/StorageUtil';

import type {AdaptiveBranchLevel, AdaptiveGuide, AdaptiveSubject} from './AdaptiveSubjects.types';

import {GROUP_BY_OPTIONS} from './constants';

const PRACTICE_SETTINGS_TOUR_KEY = 'practiceSettingsTourCompletedAt';

export type GroupByOptions = (typeof GROUP_BY_OPTIONS)[keyof typeof GROUP_BY_OPTIONS];

export type AdaptiveSubjectsFilterState = {
  filteredStandardIds: string[];
  filteredProficiencyLevels: string[];
  filteredTags: string[];
  searchQuery: string;
  hideComingSoon: boolean;
};

type SelectedSkill = {
  name: string;
  code: string;
  themeId?: string;
  topicId?: string;
  subtopicId?: string;
  subjectId?: string;
  guideId?: string;
  id: string;
  onStudentsSkillPaths?: boolean | null;
} | null;

type AdaptiveSubjectsActions = {
  getHasFilters: () => boolean;
  getFiltersSnapshot: () => AdaptiveSubjectsFilterState;
  restoreFilters: (filters: AdaptiveSubjectsFilterState) => void;
  setGroupBy: (groupBy: GroupByOptions) => void;
  setFilteredStandardIds: (standardIds: string[]) => void;
  setFilteredProficiencyLevels: (proficiencyLevels: string[]) => void;
  setFilteredTags: (tags: string[]) => void;
  setSearchQuery: (searchQuery: string) => void;
  setMatchedSearchIds: (matchedSearchIds: Set<string>) => void;
  setIsSticky: (value: boolean) => void;
  setRecentlyPracticedVisible: (isVisible: boolean) => void;
  setTagPreferencesModalOpen: (isOpen: boolean) => void;
  setSelectedSkill: (skill: SelectedSkill) => void;
  setHideComingSoon: (hide: boolean) => void;
  resetFilters: () => void;
  setPracticeSettingsTourCompletedAt: (date: string) => void;
  setGuide: (guide: AdaptiveGuide | null) => void;
  setSubject: (subject: AdaptiveSubject | null) => void;
  setLevels: (levels: AdaptiveBranchLevel[]) => void;
  setAllowedClassroomIds: (classroomIds: string[] | null) => void;
  updateSkillPathStatus: (
    subtopicId: string,
    setType: 'sent' | 'removed',
    method: 'add' | 'delete'
  ) => void;
  // Theme expand/collapse management — used by hash navigation & jump-to menu
  collapseAllThemes: () => void;
  expandTheme: (id: string) => void;
  toggleTheme: (id: string) => void;
  setForceRenderThemeId: (id: string | null) => void;
};

export type AdaptiveSubjectsState = {
  groupBy: GroupByOptions;
  filteredStandardIds: string[];
  filteredProficiencyLevels: string[];
  filteredTags: string[];
  searchQuery: string;
  matchedSearchIds: Set<string>;
  isSticky: boolean; // whether the header should be sticky
  isRecentlyPracticedVisible: boolean;
  // The "Find Skills Faster" preferences modal — shared so both the first-run
  // prompt and the filter menu's nudge banner can open it.
  isTagPreferencesModalOpen: boolean;
  selectedSkill: SelectedSkill;
  hideComingSoon: boolean; // whether to hide coming soon skills
  practiceSettingsTourCompletedAt: string;
  guide: AdaptiveGuide | null;
  subject: AdaptiveSubject | null;
  levels: AdaptiveBranchLevel[]; // added to store the levels fetched from the guide
  /**
   * Classrooms having free practice enabled for the subject, or `null` until the request settles.
   * An empty array means the teacher genuinely has none — callers must not treat the two alike.
   */
  allowedClassroomIds: string[] | null;
  skillPathStatus: {
    sentIds: Set<string>; // tracks skills that have had skill paths sent in this session (for immediate UI update without refetch),
    removedIds: Set<string>; // tracks skills that have had skill paths removed in this session
  };
  // Theme expand/collapse — centralized so hash navigation & jump-to can control it.
  // Empty object = all expanded. When a theme ID is present, that theme is collapsed.
  collapsedThemeIds: Record<string, true>;
  // When set, this theme's children bypass IntersectionObserver lazy-rendering
  // so we can scroll to a subtopic inside it.
  forceRenderThemeId: string | null;
};

const storedTourCompletedAt = local.get(PRACTICE_SETTINGS_TOUR_KEY);

const initialState: AdaptiveSubjectsState = {
  levels: [],
  groupBy: GROUP_BY_OPTIONS.SKILL,
  filteredStandardIds: [],
  filteredProficiencyLevels: [],
  filteredTags: [],
  searchQuery: '',
  matchedSearchIds: new Set(),
  isSticky: false,
  isRecentlyPracticedVisible: true,
  isTagPreferencesModalOpen: false,
  selectedSkill: null,
  hideComingSoon: false,
  practiceSettingsTourCompletedAt:
    typeof storedTourCompletedAt === 'string' ? storedTourCompletedAt : '',
  guide: null,
  subject: null,
  allowedClassroomIds: null,
  skillPathStatus: {
    sentIds: new Set<string>(),
    removedIds: new Set<string>()
  },
  collapsedThemeIds: {},
  forceRenderThemeId: null
};

export type AdaptiveSubjectsSlice = AdaptiveSubjectsState & AdaptiveSubjectsActions;

export const useAdaptiveSubjectsStore = create<AdaptiveSubjectsSlice>()(
  devtools(
    immer((set, get) => ({
      ...initialState,

      getHasFilters: () => {
        const {
          filteredStandardIds,
          filteredProficiencyLevels,
          filteredTags,
          searchQuery,
          hideComingSoon
        } = get();
        return (
          !!filteredStandardIds.length ||
          !!filteredProficiencyLevels.length ||
          !!filteredTags.length ||
          !!searchQuery ||
          hideComingSoon
        );
      },

      getFiltersSnapshot: () => {
        const {
          filteredStandardIds,
          filteredProficiencyLevels,
          filteredTags,
          searchQuery,
          hideComingSoon
        } = get();
        return {
          filteredStandardIds: [...filteredStandardIds],
          filteredProficiencyLevels: [...filteredProficiencyLevels],
          filteredTags: [...filteredTags],
          searchQuery,
          hideComingSoon
        };
      },

      restoreFilters: (filters) => {
        set((state) => {
          state.filteredStandardIds = [...filters.filteredStandardIds];
          state.filteredProficiencyLevels = [...filters.filteredProficiencyLevels];
          state.filteredTags = [...filters.filteredTags];
          state.searchQuery = filters.searchQuery;
          state.hideComingSoon = filters.hideComingSoon;
        });
      },

      setGroupBy: (groupBy) => {
        set((state) => {
          state.groupBy = groupBy;
        });
      },

      setFilteredStandardIds: (standardIds) => {
        set((state) => {
          state.filteredStandardIds = standardIds;
        });
      },

      setFilteredProficiencyLevels: (profiencyLevels) => {
        set((state) => {
          state.filteredProficiencyLevels = profiencyLevels;
        });
      },

      setFilteredTags: (tags) => {
        set((state) => {
          state.filteredTags = tags;
        });
      },

      setSearchQuery: (searchQuery) => {
        set((state) => {
          // don't query if the string is empty
          state.searchQuery = !searchQuery.trim().length ? '' : searchQuery;
          // expand all themes so search results are visible
          if (state.searchQuery) {
            state.collapsedThemeIds = {};
          }
        });
      },

      setMatchedSearchIds: (matchedSearchIds) => {
        const {searchQuery} = get();
        set((state) => {
          state.matchedSearchIds = searchQuery ? matchedSearchIds : new Set();
        });
      },

      setIsSticky: (isSticky) => {
        set((state) => {
          state.isSticky = isSticky;
        });
      },

      setSelectedSkill: (skill) => {
        set((state) => {
          state.selectedSkill = skill;
        });
      },

      setTagPreferencesModalOpen: (isOpen) => {
        set((state) => {
          state.isTagPreferencesModalOpen = isOpen;
        });
      },

      setRecentlyPracticedVisible: (isVisible) => {
        set((state) => {
          state.isRecentlyPracticedVisible = isVisible;
        });
      },

      setHideComingSoon: (hide) => {
        set((state) => {
          state.hideComingSoon = hide;
        });
      },

      setGuide: (guide) => {
        set((state) => {
          state.guide = guide;
        });
      },

      setSubject: (subject) => {
        set((state) => {
          // The modal flag is not subject-scoped, so leaving it set carries an open
          // modal onto the next subject visited without a reload (#20229). Keyed on
          // the id, not identity: a late-settling query hands down a fresh model for
          // the same subject and must not close a modal the teacher is filling in.
          if (subject?.id !== state.subject?.id) {
            state.isTagPreferencesModalOpen = false;
          }
          state.subject = subject;
        });
      },

      resetFilters: () => {
        set((state) => {
          // dont include groupBy since we want to persist the current grouping
          state.filteredStandardIds = initialState.filteredStandardIds;
          state.filteredProficiencyLevels = initialState.filteredProficiencyLevels;
          state.searchQuery = initialState.searchQuery;
          state.matchedSearchIds = initialState.matchedSearchIds;
          state.filteredTags = initialState.filteredTags;
          state.hideComingSoon = initialState.hideComingSoon;
          state.skillPathStatus = {sentIds: new Set<string>(), removedIds: new Set<string>()};
          state.collapsedThemeIds = initialState.collapsedThemeIds;
          state.forceRenderThemeId = initialState.forceRenderThemeId;
        });
      },

      setLevels: (levels) => {
        set((state) => {
          state.levels = levels;
        });
      },

      setAllowedClassroomIds: (classroomIds) => {
        // The sync hook re-derives this list on each pass, so an equal write would still re-render
        // CompareClassesCard, whose selector returns the array itself.
        const {allowedClassroomIds: current} = get();
        if (
          current === classroomIds ||
          (current && classroomIds && shallow(current, classroomIds))
        ) {
          return;
        }
        set((state) => {
          state.allowedClassroomIds = classroomIds;
        });
      },

      setPracticeSettingsTourCompletedAt: (date) => {
        local.set(PRACTICE_SETTINGS_TOUR_KEY, date);
        set((state) => {
          state.practiceSettingsTourCompletedAt = date;
        });
      },

      updateSkillPathStatus: (
        subtopicId: string,
        setType: 'sent' | 'removed',
        method: 'add' | 'delete'
      ) => {
        const {skillPathStatus} = get();
        // Only clone the modified set to avoid unnecessary reference changes
        // on the untouched set (prevents extra subscriber notifications).
        if (setType === 'sent') {
          const sentSet = new Set(skillPathStatus.sentIds);
          if (method === 'add') {
            sentSet.add(subtopicId);
          } else {
            sentSet.delete(subtopicId);
          }
          set((state) => {
            state.skillPathStatus.sentIds = sentSet;
          });
        } else if (setType === 'removed') {
          const removedSet = new Set(skillPathStatus.removedIds);
          if (method === 'add') {
            removedSet.add(subtopicId);
          } else {
            removedSet.delete(subtopicId);
          }
          set((state) => {
            state.skillPathStatus.removedIds = removedSet;
          });
        }
      },

      collapseAllThemes: () => {
        const {levels} = get();
        const allIds: Record<string, true> = {};
        for (const l of levels) {
          allIds[l.id] = true;
        }
        set((state) => {
          state.collapsedThemeIds = allIds;
        });
      },

      expandTheme: (id: string) => {
        set((state) => {
          delete state.collapsedThemeIds[id];
        });
      },

      toggleTheme: (id: string) => {
        set((state) => {
          if (state.collapsedThemeIds[id]) {
            delete state.collapsedThemeIds[id];
          } else {
            state.collapsedThemeIds[id] = true;
          }
        });
      },

      setForceRenderThemeId: (id: string | null) => {
        set((state) => {
          state.forceRenderThemeId = id;
        });
      }
    }))
  )
);
